const SUPABASE_PUBLIC_IMAGE_REGEX =
  /^(https:\/\/[^/]+\.supabase\.co\/storage\/v1)\/(object|render\/image)\/public\/(.+)$/i;

// Cloudflare CDN configuration
const CLOUDFLARE_CDN_ENABLED = () => {
  const flag =
    (import.meta.env as { VITE_CLOUDFLARE_CDN_ENABLED?: string }).VITE_CLOUDFLARE_CDN_ENABLED ??
    (import.meta.env as { PUBLIC_CLOUDFLARE_CDN_ENABLED?: string }).PUBLIC_CLOUDFLARE_CDN_ENABLED;

  if (typeof flag === 'string') {
    const normalized = flag.trim().toLowerCase();
    return ['1', 'true', 'yes', 'on', 'enabled'].includes(normalized);
  }

  // Default to true - CDN is enabled by default
  return true;
};

const CLOUDFLARE_CDN_DOMAIN = () => {
  return (
    (import.meta.env as { VITE_CLOUDFLARE_CDN_DOMAIN?: string }).VITE_CLOUDFLARE_CDN_DOMAIN ??
    (import.meta.env as { PUBLIC_CLOUDFLARE_CDN_DOMAIN?: string }).PUBLIC_CLOUDFLARE_CDN_DOMAIN ??
    'medipaths.co'
  );
};

const normalizeDimension = (value: unknown): number | undefined => {
  if (value == null) return undefined;

  if (typeof value === 'number' && Number.isFinite(value)) {
    return Math.max(1, Math.round(value));
  }

  if (typeof value === 'string') {
    const parsed = Number.parseFloat(value);
    if (Number.isFinite(parsed)) {
      return Math.max(1, Math.round(parsed));
    }
  }

  return undefined;
};

const normalizeQuality = (value: unknown): number | undefined => {
  if (value == null) return undefined;

  const numeric =
    typeof value === 'number'
      ? value
      : typeof value === 'string'
        ? Number.parseInt(value, 10)
        : undefined;

  if (!Number.isFinite(numeric) || numeric === undefined) return undefined;

  return Math.min(100, Math.max(1, Math.round(numeric)));
};

export const parseSupabasePublicImageUrl = (
  url: string
): { origin: string; variant: string; path: string; params: URLSearchParams } | null => {
  if (typeof url !== 'string' || url.length === 0) {
    return null;
  }

  const urlParts = url.split('?');
  const baseUrl = urlParts[0];
  const query = urlParts[1] ?? '';
  const match = baseUrl?.match(SUPABASE_PUBLIC_IMAGE_REGEX);
  if (!match || match.length < 4) {
    return null;
  }

  const [, origin, variant, path] = match;
  const searchParams = new URLSearchParams(query);

  return {
    origin: origin!,
    variant: variant!,
    path: path!,
    params: searchParams,
  };
};

export const isSupabasePublicImageUrl = (url: string): boolean =>
  Boolean(parseSupabasePublicImageUrl(url));

// Types for Cloudflare CDN options
interface CloudflareCDNOptions {
  width?: number;
  height?: number;
  quality?: number;
  format?: string;
  fit?: string;
  gravity?: string;
  background?: string;
  sharpen?: number | boolean;
  blur?: number;
  metadata?: string;
}

// Helper type to avoid exactOptionalPropertyTypes issues
type CloudflareCDNOptionsInput = Partial<CloudflareCDNOptions>;

// Helper functions to build transformation parameters
const addDimensionParams = (params: string[], options: CloudflareCDNOptionsInput) => {
  // Width
  if (options.width) {
    const w = normalizeDimension(options.width);
    if (w) params.push(`width=${w}`);
  }

  // Height
  if (options.height) {
    const h = normalizeDimension(options.height);
    if (h) params.push(`height=${h}`);
  }
};

const addQualityFormatParams = (params: string[], options: CloudflareCDNOptionsInput) => {
  // Quality (default to 90 for good balance between size and quality)
  const quality = normalizeQuality(options.quality ?? 90);
  if (quality) params.push(`quality=${quality}`);

  // Format (default to auto for best format selection)
  const format = options.format ?? 'auto';
  params.push(`format=${format}`);
};

const addFitModeParams = (params: string[], options: CloudflareCDNOptionsInput) => {
  // Fit mode (default to scale-down to avoid upscaling)
  const fit = options.fit ?? 'scale-down';
  params.push(`fit=${fit}`);

  // Gravity (for cropping)
  if (options.gravity) {
    params.push(`gravity=${options.gravity}`);
  }

  // Background (for pad fit mode)
  if (options.background) {
    params.push(`background=${encodeURIComponent(options.background)}`);
  }
};

const addEffectParams = (params: string[], options: CloudflareCDNOptionsInput) => {
  // Sharpening
  if (options.sharpen) {
    const sharpenValue = Math.min(10, Math.max(0, Number(options.sharpen) || 0));
    if (sharpenValue > 0) params.push(`sharpen=${sharpenValue}`);
  }

  // Blur
  if (options.blur) {
    const blurValue = Math.min(250, Math.max(0, Number(options.blur) || 0));
    if (blurValue > 0) params.push(`blur=${blurValue}`);
  }

  // Metadata (default to none for better privacy)
  const metadata = options.metadata ?? 'none';
  params.push(`metadata=${metadata}`);
};

/**
 * Build transformation parameters for Cloudflare CDN
 * @param options - Transformation options
 * @returns Array of transformation parameters
 */
const buildTransformationParams = (options: CloudflareCDNOptionsInput): string => {
  const params: string[] = [];

  addDimensionParams(params, options);
  addQualityFormatParams(params, options);
  addFitModeParams(params, options);
  addEffectParams(params, options);

  return params.join(',');
};

/**
 * Build Cloudflare CDN image transformation URL
 * @param imageUrl - Original image URL (typically Supabase)
 * @param options - Transformation options
 * @returns Cloudflare CDN URL or original URL if CDN is disabled
 */
export const buildCloudflareCDNUrl = (
  imageUrl: string,
  options: CloudflareCDNOptionsInput = {}
): string => {
  // Return original URL if CDN is disabled
  if (!CLOUDFLARE_CDN_ENABLED() || !imageUrl) {
    return imageUrl;
  }

  // Validate URL
  if (typeof imageUrl !== 'string' || imageUrl.length === 0) {
    return imageUrl;
  }

  try {
    // Build transformation parameters
    const params = buildTransformationParams(options);

    // Add onerror parameter to redirect to original image if transformation fails (9422 error)
    // This only works for same-domain images
    // Check if image is from same domain or Supabase (which we serve through our domain)
    const isSameDomain = isSupabasePublicImageUrl(imageUrl) || isSameDomainImage(imageUrl);
    const transformPath = isSameDomain ? `${params},onerror=redirect` : params;

    // Build the CDN URL
    const cdnDomain = CLOUDFLARE_CDN_DOMAIN();

    // Format: https://domain.com/cdn-cgi/image/{options}/{image-url}
    return `https://${cdnDomain}/cdn-cgi/image/${transformPath}/${imageUrl}`;
  } catch {
    return imageUrl; // Fallback to original URL on error
  }
};

/**
 * Check if a URL is already a Cloudflare CDN URL
 * @param {string} url - URL to check
 * @returns {boolean}
 */
export const isCloudflareCDNUrl = (url: string): boolean => {
  if (typeof url !== 'string') return false;
  return url.includes('/cdn-cgi/image/');
};

/**
 * Extract the original image URL from a Cloudflare CDN URL
 * @param {string} cdnUrl - Cloudflare CDN URL
 * @returns {string|null} - Original image URL or null if not a valid CDN URL
 */
export const extractOriginalUrlFromCDN = (cdnUrl: string): string | null => {
  if (!isCloudflareCDNUrl(cdnUrl)) return null;

  try {
    // Format: https://domain.com/cdn-cgi/image/{options}/{original-url}
    const cdnPattern = /\/cdn-cgi\/image\/[^/]+\/(.+)$/;
    const match = cdnUrl.match(cdnPattern);

    if (match?.[1]) {
      return match[1];
    }

    return null;
  } catch {
    return null;
  }
};

/**
 * Check if a URL is from the same domain as the CDN domain
 * @param {string} url - URL to check
 * @returns {boolean}
 */
export const isSameDomainImage = (url: string): boolean => {
  if (typeof url !== 'string' || url.length === 0) return false;

  try {
    const cdnDomain = CLOUDFLARE_CDN_DOMAIN();
    const urlObj = new URL(url);

    // Check if the URL is from Supabase storage (always same domain for CDN purposes)
    if (isSupabasePublicImageUrl(url)) {
      return true;
    }

    // Check if the URL hostname matches or ends with the CDN domain
    return urlObj.hostname === cdnDomain || urlObj.hostname.endsWith(`.${cdnDomain}`);
  } catch {
    // If URL parsing fails, assume it's not same domain
    return false;
  }
};

const shouldUseImageRenderer = () => {
  const flag =
    (import.meta.env as { VITE_SUPABASE_IMAGE_TRANSFORMER?: string })
      .VITE_SUPABASE_IMAGE_TRANSFORMER ??
    (import.meta.env as { PUBLIC_SUPABASE_IMAGE_TRANSFORMER?: string })
      .PUBLIC_SUPABASE_IMAGE_TRANSFORMER ??
    (import.meta.env as { VITE_SUPABASE_IMAGE_RENDERER?: string }).VITE_SUPABASE_IMAGE_RENDERER ??
    (import.meta.env as { PUBLIC_SUPABASE_IMAGE_RENDERER?: string }).PUBLIC_SUPABASE_IMAGE_RENDERER;

  if (typeof flag === 'string') {
    const normalized = flag.trim().toLowerCase();
    if (['1', 'true', 'yes', 'on', 'enabled'].includes(normalized)) {
      return true;
    }
    if (['0', 'false', 'no', 'off', 'disabled'].includes(normalized)) {
      return false;
    }
  }

  // Default to false to preserve compatibility unless explicitly enabled
  return false;
};

// Types for Supabase image options
interface SupabaseImageOptions {
  width?: number;
  height?: number;
  format?: string;
  quality?: number;
  resize?: string;
  useCDN?: boolean;
}

// Helper types to avoid exactOptionalPropertyTypes issues
type SupabaseImageOptionsInput = Partial<SupabaseImageOptions>;
type SupabaseImageOptionsWithoutCDN = Omit<SupabaseImageOptions, 'useCDN'>;
type SupabaseImageOptionsWithoutCDNInput = Partial<SupabaseImageOptionsWithoutCDN>;

/**
 * Map Supabase resize modes to Cloudflare fit modes
 * @param resize - Supabase resize mode
 * @returns Cloudflare fit mode
 */
const mapSupabaseResizeToCloudflareFit = (resize?: string): string => {
  if (resize === 'cover') return 'cover';
  if (resize === 'contain') return 'contain';
  if (resize === 'fill') return 'crop';
  return 'scale-down'; // default
};

// Helper functions for parameter management
const setOrDeleteParam = (params: URLSearchParams, key: string, value?: string | number) => {
  if (value != null) {
    params.set(key, String(value));
  } else if (params.has(key)) {
    params.delete(key);
  }
};

const calculateEffectiveResize = (
  resize: string | undefined,
  normalizedWidth: number | undefined,
  normalizedHeight: number | undefined
) => {
  return resize ?? (normalizedWidth && normalizedHeight ? 'cover' : undefined);
};

/**
 * Build Supabase native transformation URL
 * @param url - Original URL
 * @param options - Transformation options
 * @returns Transformed Supabase URL
 */
const buildSupabaseNativeUrl = (
  url: string,
  { width, height, format, quality, resize }: SupabaseImageOptionsWithoutCDNInput
): string => {
  const parsed = parseSupabasePublicImageUrl(url);

  if (!parsed) {
    return url;
  }

  const normalizedWidth = normalizeDimension(width);
  const normalizedHeight = normalizeDimension(height);
  const normalizedQuality = normalizeQuality(quality);

  const params = parsed.params;

  // Set dimension parameters
  setOrDeleteParam(params, 'width', normalizedWidth);
  setOrDeleteParam(params, 'height', normalizedHeight);

  // Set format and quality parameters
  setOrDeleteParam(params, 'format', format);
  setOrDeleteParam(params, 'quality', normalizedQuality);

  // Calculate and set resize parameter
  const effectiveResize = calculateEffectiveResize(resize, normalizedWidth, normalizedHeight);
  setOrDeleteParam(params, 'resize', effectiveResize);

  const serializedParams = params.toString();
  const useRenderer = shouldUseImageRenderer();
  const variant = useRenderer ? 'render/image' : parsed.variant;
  const baseUrl = `${parsed.origin}/${variant}/public/${parsed.path}`;

  return serializedParams ? `${baseUrl}?${serializedParams}` : baseUrl;
};

// Helper functions for building options objects
const buildCDNOptions = (
  width: number | undefined,
  height: number | undefined,
  format: string | undefined,
  quality: number | undefined,
  resize: string | undefined
): CloudflareCDNOptionsInput => {
  const fit = mapSupabaseResizeToCloudflareFit(resize);
  const options: Record<string, unknown> = { fit };

  if (width !== undefined) options.width = width;
  if (height !== undefined) options.height = height;
  if (format !== undefined) options.format = format;
  if (quality !== undefined) options.quality = quality;

  return options as CloudflareCDNOptionsInput;
};

const buildNativeOptions = (
  width: number | undefined,
  height: number | undefined,
  format: string | undefined,
  quality: number | undefined,
  resize: string | undefined
): SupabaseImageOptionsWithoutCDNInput => {
  const options: Record<string, unknown> = {};

  if (width !== undefined) options.width = width;
  if (height !== undefined) options.height = height;
  if (format !== undefined) options.format = format;
  if (quality !== undefined) options.quality = quality;
  if (resize !== undefined) options.resize = resize;

  return options as SupabaseImageOptionsWithoutCDNInput;
};

const shouldUseCDN = (useCDN: boolean | undefined, url: string): boolean => {
  return (useCDN ?? true) && CLOUDFLARE_CDN_ENABLED() && !isCloudflareCDNUrl(url);
};

export const buildSupabaseImageUrl = (
  url: string,
  { width, height, format, quality, resize, useCDN = true }: SupabaseImageOptionsInput = {}
): string => {
  // If CDN is enabled and requested, use Cloudflare CDN transformation
  if (shouldUseCDN(useCDN, url)) {
    const cdnOptions = buildCDNOptions(width, height, format, quality, resize);
    return buildCloudflareCDNUrl(url, cdnOptions);
  }

  // Fallback to Supabase native transformation
  const nativeOptions = buildNativeOptions(width, height, format, quality, resize);
  return buildSupabaseNativeUrl(url, nativeOptions);
};

// Types for srcSet options
interface SrcSetOptions extends Omit<SupabaseImageOptions, 'width'> {
  height?: number;
}

type SrcSetOptionsInput = Partial<SrcSetOptions>;

export const buildSupabaseSrcSet = (
  url: string,
  widths: number[],
  { format, quality, resize, height, useCDN = true }: SrcSetOptionsInput = {}
): string | null => {
  if (!Array.isArray(widths) || widths.length === 0) {
    return null;
  }

  const parsed = parseSupabasePublicImageUrl(url);
  if (!parsed) {
    return null;
  }

  const entries = widths
    .map(candidate => {
      const normalized = normalizeDimension(candidate);
      if (!normalized) return null;

      const imageOptions: Record<string, unknown> = {
        width: normalized,
        useCDN,
      };
      if (height !== undefined) imageOptions.height = height;
      if (format !== undefined) imageOptions.format = format;
      if (quality !== undefined) imageOptions.quality = quality;
      if (resize !== undefined) imageOptions.resize = resize;

      const transformed = buildSupabaseImageUrl(url, imageOptions as SupabaseImageOptionsInput);

      return `${transformed} ${normalized}w`;
    })
    .filter(Boolean);

  return entries.length > 0 ? entries.join(', ') : null;
};
